"use client";

import * as Sentry from "@sentry/nextjs";
import Link from "next/link";
import { useEffect } from "react";

const SAFE_ERROR_TYPES = new Set([
  "AggregateError",
  "Error",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TypeError",
  "URIError",
]);

function getSafeErrorType(error: Error): string {
  return SAFE_ERROR_TYPES.has(error.name) ? error.name : "OtherError";
}

export default function GlobalError({
  error,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    try {
      Sentry.captureException(error);
    } catch {
      // A reporting failure must not replace the recovery screen.
    }

    // Fire a GA4 event if gtag is already loaded (global-error renders
    // outside the normal layout, so LateGA is not present).
    try {
      if (typeof window === "undefined") return;
      const w = window as Window & { gtag?: (...args: unknown[]) => void };
      w.gtag?.("event", "app_error", {
        error_digest: error.digest ?? "unknown",
        error_digest_present: error.digest ? 1 : 0,
        error_boundary: "global",
        error_type: getSafeErrorType(error),
      });
    } catch {
      // Keep recovery available even when analytics is blocked or broken.
    }
  }, [error]);

  return (
    <html lang="en">
      <body className="m-0 bg-slate-50 text-slate-950">
        <main
          id="main"
          className="mx-auto flex min-h-screen max-w-xl flex-col justify-center px-6 py-16"
        >
          <p className="text-sm font-semibold uppercase tracking-wide text-blue-700">
            Eclat Institute
          </p>
          <h1 className="mt-3 text-3xl font-bold">Something went wrong</h1>
          <p className="mt-4 text-base leading-7 text-slate-700">
            We could not load this page. You can try again without re-entering
            the address.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={() => {
                // A boundary reset can reuse a rejected RSC promise.
                window.location.reload();
              }}
              className="rounded-lg bg-blue-700 px-5 py-3 font-semibold text-white"
            >
              Try again
            </button>
            <Link
              href="/"
              className="rounded-lg border border-slate-300 bg-white px-5 py-3 font-semibold text-slate-900"
            >
              Go to homepage
            </Link>
          </div>
        </main>
      </body>
    </html>
  );
}
